/* global Vue, PREFIX, IS_BOOKING, WWW_HOME_URL */

import Modal from './Modal'
import { startLoading, setErrorAndStopLoading, stopLoading } from './Notifications'
import AuthService from '../../AuthService'
import { useAuthObserver } from '../../use-auth-observer'

export default {
  name: 'AuthStatus',
  components: { Modal },
  template: `
    <div class="header__profile">
      <button
        v-if="!isAuthenticated && !isLoading"
        @click="isSplitOpen = true"
        class="header__icon header__icon--user"
        :title="$t('authStatus.login')">
        <i class="icon-user"></i>
      </button>

      <button
        v-else-if="isAuthenticated && !isLoading"
        @click="showMenu = !showMenu"
        class="header__icon header__icon--user"
        ref="loyaltyIcon"
        :title="showMenu ? $t('authStatus.closeMenu') : $t('authStatus.openMenu')">
        <span class="hide-xs-up" data-sl="mask">{{ adjustedDisplayName('initials') }}</span>
        <span class="hide-xs-down hide-md-up" data-sl="mask">{{ adjustedDisplayName('short') }}</span>
        <span class="hide-md-down" data-sl="mask">{{ adjustedDisplayName('full') }}</span>
        <i :class="showMenu ? 'active' : ''" class="icon-caret-down"></i>
      </button>

      <div v-else class="header__icons--dots">&nbsp;</div>

      <transition name="fade">
        <nav v-show="showMenu" class="header__loyalty-nav" ref="loyaltyNav">
          <template v-if="hasLoyaltyProfile && !IS_BOOKING">
            <a :href="profileUrl + '#'" @click="showMenu = false">{{ $t('authStatus.myProfile') }}</a>
            <a :href="profileUrl + '#/reservations'" @click="showMenu = false">{{ $t('authStatus.myBookings') }}</a>
            <a v-if="!isMaiFamily" :href="profileUrl + '#/activity'" @click="showMenu = false">{{ $t('authStatus.pointsActivity') }}</a>
            <a :href="profileUrl + '#/awards'" @click="showMenu = false">{{ $t('authStatus.MaiStarRewards') }}</a>
          </template>
          <hr v-if="hasLoyaltyProfile && !IS_BOOKING">
          <button @click="$AuthService.signOut()">{{ $t('authStatus.logout') }}</button>
        </nav>
      </transition>
    </div>

    <teleport to="body">
      <Modal v-if="!isAuthenticated" :isOpen="isSplitOpen" @closeModal="isSplitOpen = false" :title="$t('loyaltySplit.title')">
        <div class="grid login-split">
          <div class="col-12 col-sm-6 col-md-5 col-xl-4 offset-md-7 offset-xl-8 order-sm-2 login-split__auth">
            <h4>{{ $t('loyaltySplit.accessAccount') }}</h4>
            <p>{{ $t('loyaltySplit.signInText') }}</p>
            <button class="button button--maistar button--block" @click="e => { startLoading('authStatus'); $AuthService.signIn() }">
              {{ $t('loyaltySplit.signIn') }}
            </button>

            <h4>{{ $t('loyaltySplit.newHere') }}</h4>
            <p>{{ $t('loyaltySplit.joinText') }}</p>
            <button class="button button--outline button--block" @click="redirectToSignup">
              {{ $t('loyaltySplit.register') }}
            </button>
          </div>

          <div class="col-12 col-sm-6 order-sm-1 login-split__benefits">
            <p>{{ $t('loyaltySplit.accessText') }}</p>
            <ul class="no-bullets" v-html="$t('loyaltySplit.benefits')"></ul>
          </div>
        </div>
      </Modal>
    </teleport>
  `,
  setup (props, { emit }) {
    const isLoading = Vue.ref(true)
    const isModalOpen = Vue.ref(false)
    const isSplitOpen = Vue.ref(false)
    const showMenu = Vue.ref(false)
    const displayName = Vue.ref('')
    const loyaltyId = Vue.ref(null)
    const loyaltyIcon = Vue.ref(null)
    const loyaltyNav = Vue.ref(null)
    const { authObserver } = useAuthObserver()

    const isAuthenticated = Vue.ref(false)
    const hasLoyaltyProfile = Vue.computed(() => loyaltyId.value !== null)
    const isMaiFamily = Vue.ref(AuthService.isMaiFamily)
    const profileUrl = Vue.computed(() => PREFIX + (isMaiFamily.value ? '/maifamily/' : '/loyalty/profile/'))

    function adjustedDisplayName (length) {
      switch (length) {
        case 'initials':
          return displayName.value.split(' ').map(word => word[0]).join('')
        case 'short':
          return displayName.value.split(' ')[0]
        default:
          return displayName.value
      }
    }

    function redirectToSignup () {
      startLoading('authStatus')
      const redirectToProfile = IS_BOOKING && !!location.href.match(/\/step5/)
      AuthService.redirectSignup(redirectToProfile ? { redirectUri: `${WWW_HOME_URL.replace(/\/+$/, '')}${PREFIX}/loyalty/profile/` } : {})
    }

    Vue.watch(showMenu, (show) => {
      if (show && window.matchMedia('(max-width: 768px)').matches) {
        document.body.classList.add('overflow-hidden')
      } else {
        document.body.classList.remove('overflow-hidden')
      }
    })

    document.addEventListener('click', (event) => {
      if (
        showMenu.value &&
        !loyaltyIcon.value.contains(event.target) &&
        !loyaltyNav.value.contains(event.target)
      ) {
        showMenu.value = false
      }
    }, { passive: true })

    Vue.onMounted(() => {
      AuthService.on('ended', e => { isLoading.value = false })
      if (AuthService.isFinished) {
        isLoading.value = false
      }
      AuthService.on('error', e => {
        console.error(e)
        isLoading.value = false
        setErrorAndStopLoading(e.detail)
      })
      AuthService.on('authenticated', e => {
        displayName.value = e.detail.displayName
        isLoading.value = false
        isAuthenticated.value = true
        isMaiFamily.value = !!e.detail.isMaiFamily
        if (!Number.isInteger(e.detail.loyaltyId)) {
          console.warn('Incomplete profile')
        } else {
          loyaltyId.value = e.detail.loyaltyId
        }

        document.body.dataset.authentication = JSON.stringify(e.detail)
      })
      if (AuthService.isAuthenticated) {
        isAuthenticated.value = true
        displayName.value = AuthService.getDisplayName()
        isLoading.value = false
        isMaiFamily.value = !!AuthService.isMaiFamily

        document.body.dataset.authentication = JSON.stringify({
          displayName: AuthService.getDisplayName(),
          accountId: AuthService.accountId,
          loyaltyId: AuthService.loyaltyId,
          isMaiFamily: AuthService.isMaiFamily,
        })
      }
    })

    Vue.watch(authObserver.signInRequested, () => {
      AuthService.signIn()
    })

    // bfcache restore after B2C redirect - look at AuthService.js
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        stopLoading('authStatus')
        isSplitOpen.value = false
        isLoading.value = false
      }
    })

    return {
      PREFIX,
      IS_BOOKING,
      isLoading,
      isAuthenticated,
      hasLoyaltyProfile,
      isMaiFamily,
      profileUrl,
      isModalOpen,
      isSplitOpen,
      showMenu,
      loyaltyIcon,
      loyaltyNav,
      startLoading,
      redirectToSignup,
      adjustedDisplayName,
    }
  },
}
